// client/table/header-line.ts
import { css } from "@adornis/chemistry/directives/css.js";
import "@adornis/chemistry/elements/components/x-flex.js";
import "@adornis/chemistry/elements/components/x-grid.js";
import { DesignSystem } from "@adornis/chemistry/elements/theming/design.js";
import { FieldConfiguration } from "@adornis/field-management/field-configuration.js";
import { translateFieldName } from "@adornis/field-management/translate-field.js";
import { stateful, useEffect, useMemo, useObservable, useState } from "@adornis/functional-lit/stateful.js";
import "@adornis/popover/x-dropdown";
import { clientTranslator } from "@adornis/translation-core/client/clientTranslator.js";
import { html, nothing } from "lit";
import { createRef, ref } from "lit/directives/ref.js";
import { repeat } from "lit/directives/repeat.js";
import { XSortingCard } from "./x-sorting-card.js";
var DataViewHeader = stateful(
  ({
    options,
    finalColumns,
    onSort,
    onColumnsUpdated,
    gridElement
  }) => {
    const [mouseMoveColIndex, setMouseMoveColIndex] = useState(-1);
    const [mouseMoveStartX, setMouseMoveStartX] = useState(0);
    const [draggingClippedOnMin, setDraggingClippedOnMin] = useState(false);
    const design = useObservable(() => DesignSystem.currentTheme, []);
    const theadRef = useMemo(() => [createRef()], []);
    const [columnWidths, setColumnWidths] = useState([]);
    let lastCalculatedWidths = [];
    useEffect(() => {
      const thead = theadRef.value;
      if (thead) {
        const initialWidths = getColWidths();
        setColumnWidths(initialWidths);
        lastCalculatedWidths = [...initialWidths];
        if (gridElement.value) {
          gridElement.value.style.gridTemplateColumns = initialWidths.map((w) => w + "px").join(" ");
        }
      }
    }, [theadRef.value]);
    if (!gridElement.value)
      throw new Error("Grid element not found");
    const getColWidths = () => {
      const thead = theadRef.value;
      if (!thead)
        return [];
      return Array.from(thead.querySelectorAll("[cell]") ?? []).map((col) => col.offsetWidth);
    };
    const mouseMoveHandler = (e) => {
      e.preventDefault();
      if (mouseMoveColIndex === -1)
        return;
      const deltaX = e.clientX - mouseMoveStartX;
      if (draggingClippedOnMin && deltaX < 0)
        return;
      const newColumnWidths = [...columnWidths];
      const currentWidth = columnWidths[mouseMoveColIndex] || 100;
      let newWidth = Math.max(currentWidth + deltaX, 30);
      setDraggingClippedOnMin(newWidth <= 30);
      newColumnWidths[mouseMoveColIndex] = newWidth;
      const grid = gridElement.value;
      if (grid) {
        grid.style.gridTemplateColumns = newColumnWidths.map((w) => w + "px").join(" ");
      }
      lastCalculatedWidths = [...newColumnWidths];
      setColumnWidths(newColumnWidths);
      setMouseMoveStartX(e.clientX);
    };
    const mouseUpHandler = () => {
      if (mouseMoveColIndex === -1)
        return;
      if (onColumnsUpdated && lastCalculatedWidths.length > 0) {
        onColumnsUpdated(lastCalculatedWidths);
      } else if (onColumnsUpdated && columnWidths.length > 0) {
        onColumnsUpdated(columnWidths);
      }
      setMouseMoveColIndex(-1);
      setMouseMoveStartX(0);
      setDraggingClippedOnMin(false);
    };
    useEffect(() => {
      if (mouseMoveColIndex !== -1) {
        document.addEventListener("mousemove", mouseMoveHandler);
        document.addEventListener("mouseup", mouseUpHandler);
        return () => {
          document.removeEventListener("mousemove", mouseMoveHandler);
          document.removeEventListener("mouseup", mouseUpHandler);
        };
      }
    }, [mouseMoveColIndex]);
    const getHeaderContextMenuItems = (colDef, close) => {
      const buttonStyle = {
        padding: design?.spacing.xs,
        borderBottom: `1px solid var(--color-base-100)`,
        borderRadius: "0"
      };
      const items = [
        options.isSortable && (colDef.fieldName || colDef.isSortable === true) ? html`
              <x-button
                ${css(buttonStyle)}
                mode="text"
                icon="sort"
                @click=${() => {
          const filter = options.view?.filter;
          if (!filter)
            throw new Error("Filter is required for sorting");
          onSort?.(
            new CustomEvent("sort", {
              detail: {
                columnFieldName: colDef.fieldName,
                sortingDirection: "asc"
              }
            })
          );
          close();
        }}
              >
                ${clientTranslator.translate("data-view", "sort")}
              </x-button>
            ` : null,
        !colDef.isAlwaysVisible && options.isColumnSelectable ? html`
              <x-button
                ${css(buttonStyle)}
                mode="text"
                icon="delete"
                @click=${() => {
          options.view?.configuredColumns?.splice(
            options.view.configuredColumns.findIndex(
              (confCol) => FieldConfiguration.resolveID(confCol) === colDef._id
            ),
            1
          );
          close();
        }}
              >
                ${clientTranslator.translate("data-view", "delete")}
              </x-button>
            ` : null
      ].filter((v) => v);
      return items;
    };
    const sortingIconDisplay = (colDef) => {
      if (!options.isSortable || !options.view?.filter)
        return nothing;
      const filter = options.view.filter;
      const sortingInformationIndex = filter.sortingFields.findIndex(
        (si) => si === (colDef.customID ?? colDef.fieldName)
      );
      return html`
        <div
          @click=${(e) => {
        e.stopPropagation();
        const newDirection = filter.sortingDirections[sortingInformationIndex] === "asc" ? "desc" : "asc";
        onSort?.(
          new CustomEvent("sort", {
            detail: {
              columnIndex: sortingInformationIndex,
              columnFieldName: colDef.fieldName ?? colDef.customID,
              sortingDirection: newDirection
            }
          })
        );
      }}
        >
          ${sortingInformationIndex !== -1 ? XSortingCard({
        sortingDirection: filter.sortingDirections[sortingInformationIndex],
        sortingNumber: sortingInformationIndex,
        onUpdated: (e) => {
          onSort?.(
            new CustomEvent("sort", {
              detail: {
                columnIndex: sortingInformationIndex,
                columnFieldName: colDef.fieldName ?? colDef.customID,
                sortingDirection: e.detail.sortingDirection
              }
            })
          );
        }
      }) : html` <x-icon> sort </x-icon> `}
        </div>
      `;
    };
    const getLeftOffsetForStackedStickyColumns = (colDef, colIndex) => {
      if (!colDef.isSticky)
        return "0px";
      const leftOffset = finalColumns.slice(0, colIndex).reduce((acc, curr) => acc + +curr.columnWidth.replace("px", "") + 3, 0);
      return leftOffset.toString() + "px";
    };
    return html`
      <style>
        [cell] {
          position: relative;

          [resize-handle] {
            content: '';
            position: absolute;
            width: 4px;
            height: 100%;
            top: 0;
            right: 0;
            cursor: col-resize;
            background-color: color-mix(in oklch, var(--color-base-content) 5%, #0000);
            margin-inline: 0 auto;
            transition: all 0.125s ease-in;
            -webkit-touch-callout: none;
            -webkit-user-select: none;
            user-select: none;
          }
        }
      </style>
      <div ${ref(theadRef)} class="grid grid-cols-subgrid col-span-full sticky top-0 bg-base-100 z-10">
        ${repeat(
      finalColumns,
      (colDef) => colDef,
      (colDef, indexColumn) => {
        const columnName = colDef.columnName !== null ? colDef.columnName : colDef.fieldName ? translateFieldName(colDef.fieldName) : "";
        return html`
              <div
                class="py-2 px-4 flex items-center overflow-hidden"
                cell
                data-column=${indexColumn}
                ${css({
          width: columnWidths[indexColumn] ? columnWidths[indexColumn] + "px" : colDef.columnWidth,
          [colDef.position === "back" ? "right" : "left"]: colDef.isSticky ? colDef.position === "back" ? "0" : getLeftOffsetForStackedStickyColumns(colDef, indexColumn) : "unset",
          zIndex: colDef.isSticky ? "10" : "unset",
          "-webkit-touch-callout": "none",
          "-webkit-user-select": "none",
          "user-select": "none"
        })}
              >
                <div
                  ?resize-handle=${options.isColumnResizable}
                  @mousedown=${(e) => {
          e.stopPropagation();
          const currentElement = e.currentTarget?.closest("[cell]");
          if (!currentElement)
            return;
          const currentWidth = currentElement.offsetWidth;
          const newWidths = [...columnWidths];
          while (newWidths.length <= indexColumn) {
            newWidths.push(100);
          }
          newWidths[indexColumn] = currentWidth;
          setColumnWidths(newWidths);
          setMouseMoveStartX(e.clientX);
          setDraggingClippedOnMin(false);
          setMouseMoveColIndex(indexColumn);
        }}
                ></div>
                <div>
                  <x-dropdown
                    .items=${(close) => getHeaderContextMenuItems(colDef, close)}
                    placement="bottom-start"
                  >
                    <div class="flex flex-row items-center justify-center gap-2">
                      <span class="flex-1">${columnName}</span> ${sortingIconDisplay(colDef)}
                    </div>
                  </x-dropdown>
                </div>
              </div>
            `;
      }
    )}
      </div>
    `;
  }
);
export {
  DataViewHeader
};
